import * as React from "react";

import { VizConfig } from "@/shared/config";
import { FileRecord } from "@/shared/domain/files";
import { createEmptyLayout, LayoutRecord } from "@/shared/domain/layouts";
import { TopicRecord, MessagePathRecord } from "@/shared/domain/topics";
import { State } from "@/shared/state/visualization";

import { MatchError } from "../layouts/LayoutToVizConfig";
import { Timer } from "../timer";

import { DEFAULT_TIME_DISPLAY_MODE, TimeDisplayMode } from "./TimeDisplayMode";

const noop = () => {};
const noopLayout = createEmptyLayout();

export interface SetActiveLayoutOptions {
  skipReload?: boolean;
}

export interface EphemeralWorkspaceState {
  // Server state
  files: FileRecord[];
  topics: TopicRecord[];
  messagePaths: MessagePathRecord[];
  isDataLoading: boolean;
  error?: Error | null;
  layoutError?: Error | null;

  // Panel selection
  selectedPanelId: string | null;
  selectPanel: (panelId: string) => void;
  clearPanelSelection: () => void;
  clearPanelSelectionIfMatches: (panelId: string) => void; // compare-and-set

  // Timer
  timer: Timer;

  // Hydration from a Layout or SharedWorkspace:
  isHydrating: boolean;
  setIsHydrating: (value: boolean) => void;

  // Active layout
  activeLayout: LayoutRecord;
  setActiveLayout: (
    layout: LayoutRecord,
    options?: SetActiveLayoutOptions,
  ) => void;
  skipLayoutRehydration: boolean;
  workspaceSnapshot: {
    panels: State["panels"];
    layout: State["layout"];
  };
  setWorkspaceSnapshot: (snapshot: {
    panels: State["panels"];
    layout: State["layout"];
  }) => void;

  // Panel data errors
  dataMatchingErrors: Record<string, MatchError[]>;
  setDataMatchingErrors: (errors: Record<string, MatchError[]>) => void; // panelId -> MatchErrors

  // Time display mode (Elapsed/Log/Local)
  timeDisplayMode: TimeDisplayMode;
  setTimeDisplayMode: (mode: TimeDisplayMode) => void;
}

export const EphemeralWorkspaceStateContext =
  React.createContext<EphemeralWorkspaceState>({
    files: [],
    topics: [],
    messagePaths: [],
    isDataLoading: false,
    error: null,
    layoutError: null,
    selectedPanelId: null,
    selectPanel: noop,
    clearPanelSelection: noop,
    clearPanelSelectionIfMatches: noop,
    timer: new Timer({ spans: [] }),
    activeLayout: noopLayout,
    setActiveLayout: noop,
    skipLayoutRehydration: false,
    isHydrating: false,
    setIsHydrating: noop,
    dataMatchingErrors: {},
    setDataMatchingErrors: noop,
    workspaceSnapshot: {
      panels: VizConfig.default().panels,
      layout: VizConfig.default().layout,
    },
    setWorkspaceSnapshot: noop,
    timeDisplayMode: DEFAULT_TIME_DISPLAY_MODE,
    setTimeDisplayMode: noop,
  });
